import React, { useCallback, useState } from 'react';
import { Overlay } from 'react-bootstrap';

interface MenuItem {
  label: string;
  onClick: (itemId: string) => void;
}

interface DropdownState {
  target: HTMLElement | null;
  itemId: string | null;
}

/** Manages a single dropdown overlay shared across all list items to avoid mounting one per row. */
const useSharedDropdown = () => {
  const [state, setState] = useState<DropdownState>({ target: null, itemId: null });

  const toggleMenu = useCallback(
    (e: React.MouseEvent, itemId: string) => {
      e.stopPropagation();
      const isSameItem = state.itemId === itemId && state.target !== null;
      if (isSameItem) {
        setState({ target: null, itemId: null });
      } else {
        setState({ target: e.currentTarget as HTMLElement, itemId });
      }
    },
    [state.itemId, state.target],
  );

  const closeMenu = useCallback(() => {
    setState({ target: null, itemId: null });
  }, []);

  const renderMenu = useCallback(
    (items: MenuItem[]) => (
      <Overlay
        target={state.target}
        show={!!state.target}
        placement="bottom-end"
        rootClose
        onHide={closeMenu}
        transition={false}
      >
        {({ placement, arrowProps, show: _show, popper, ...props }) => (
          <div
            {...props}
            className="dropdown-menu show"
            style={{
              ...props.style,
              minWidth: '8rem',
              padding: 0,
              zIndex: 1050,
            }}
          >
            {items.map((item) => (
              <button
                key={item.label}
                type="button"
                className="dropdown-item"
                onClick={() => {
                  if (state.itemId) {
                    item.onClick(state.itemId);
                  }
                  closeMenu();
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </Overlay>
    ),
    [state.target, state.itemId, closeMenu],
  );

  return {
    toggleMenu,
    closeMenu,
    activeItemId: state.itemId,
    renderMenu,
  } as const;
};

export default useSharedDropdown;
